import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash,
  ArrowLeft,
  Loader as LoaderIcon,
  MoreVertical,
  CheckCircle2,
  Clock3,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  getAdminManagedCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  approveCategory,
  markCategoryPending,
  BlogCategory,
} from '../../../services/Writer/blogCategoriesService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ManageBlogCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [markingPending, setMarkingPending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await getAdminManagedCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      setSubmitting(true);
      const newCategory = await createCategory({ name: formData.name.trim() });
      setCategories([newCategory, ...categories]);
      setFormData({ name: '' });
      setShowAddModal(false);
      toast.success('Category created successfully');
    } catch (error) {
      console.error('Error creating category:', error);
      const message = getApiErrorMessage(error, 'Failed to create category');
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !selectedCategory) {
      toast.error('Category name is required');
      return;
    }

    try {
      setSubmitting(true);
      const updatedCategory = await updateCategory(selectedCategory.categoryId, {
        name: formData.name.trim(),
      });
      setCategories(
        categories.map((cat) =>
          cat.categoryId === selectedCategory.categoryId ? updatedCategory : cat
        )
      );
      setFormData({ name: '' });
      setSelectedCategory(null);
      setShowEditModal(false);
      toast.success('Category updated successfully');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      setDeleting(true);
      await deleteCategory(selectedCategory.categoryId);
      setCategories(categories.filter((cat) => cat.categoryId !== selectedCategory.categoryId));
      setSelectedCategory(null);
      setShowDeleteModal(false);
      toast.success('Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  const handleApproveCategory = async () => {
    if (!selectedCategory) return;
    try {
      setApproving(true);
      const updated = await approveCategory(selectedCategory.categoryId);
      setCategories((prev) =>
        prev.map((cat) => (cat.categoryId === selectedCategory.categoryId ? updated : cat))
      );
      setShowApproveModal(false);
      setSelectedCategory(null);
      toast.success('Category approved');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to approve category'));
    } finally {
      setApproving(false);
    }
  };

  const handleMarkPendingCategory = async () => {
    if (!selectedCategory) return;
    try {
      setMarkingPending(true);
      const updated = await markCategoryPending(selectedCategory.categoryId);
      setCategories((prev) =>
        prev.map((cat) => (cat.categoryId === selectedCategory.categoryId ? updated : cat))
      );
      setShowPendingModal(false);
      setSelectedCategory(null);
      toast.success('Category marked as pending');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to mark category as pending'));
    } finally {
      setMarkingPending(false);
    }
  };

  const openEditModal = (category: BlogCategory) => {
    setSelectedCategory(category);
    setFormData({ name: category.name });
    setShowEditModal(true);
  };

  const openDeleteModal = (category: BlogCategory) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const openApproveModal = (category: BlogCategory) => {
    setSelectedCategory(category);
    setShowApproveModal(true);
  };

  const openPendingModal = (category: BlogCategory) => {
    setSelectedCategory(category);
    setShowPendingModal(true);
  };

  const closeModals = () => {
    if (approving || markingPending || deleting || submitting) return;
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowApproveModal(false);
    setShowPendingModal(false);
    setSelectedCategory(null);
    setFormData({ name: '' });
  };

  const statusBadge = (status?: string) => {
    const value = status || 'approved';
    const classes =
      value === 'approved'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
        : value === 'pending'
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
    return (
      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ${classes}`}>
        {value}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/writer/blogs')}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-400 dark:to-primary-600 bg-clip-text text-transparent">
                Manage Blog Categories
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2 text-lg">
                Organize your blog content with categories
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {categories.length} {categories.length === 1 ? 'category' : 'categories'} total
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm"
            >
              <Plus className="w-5 h-5" />
              Add Category
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center h-96">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <LoaderIcon className="w-6 h-6 text-primary-600" />
              </div>
            </div>
            <p className="text-gray-500 mt-4 text-lg font-medium">Loading categories...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => {
              const canMarkPending = category.approvalStatus !== 'pending';
              return (
                <div
                  key={category.categoryId}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between mb-4 gap-3">
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-2">
                        {category.name}
                      </h3>
                      <div className="mt-2">{statusBadge(category.approvalStatus)}</div>
                    </div>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                          aria-label="Actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          sideOffset={8}
                          align="end"
                          className="min-w-[180px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-1 text-sm z-50"
                        >
                          <DropdownMenu.Item
                            onSelect={() => openEditModal(category)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 outline-none"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </DropdownMenu.Item>

                          {category.approvalStatus !== 'approved' ? (
                            <DropdownMenu.Item
                              onSelect={() => openApproveModal(category)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 outline-none"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              Approve
                            </DropdownMenu.Item>
                          ) : null}

                          {canMarkPending ? (
                            <DropdownMenu.Item
                              onSelect={() => openPendingModal(category)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 outline-none"
                            >
                              <Clock3 className="w-4 h-4" />
                              Mark as Pending
                            </DropdownMenu.Item>
                          ) : null}

                          <DropdownMenu.Separator className="h-px bg-gray-200 dark:bg-gray-700 my-1" />
                          <DropdownMenu.Item
                            onSelect={(e) => {
                              e.preventDefault();
                              openDeleteModal(category);
                            }}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 outline-none"
                          >
                            <Trash className="w-4 h-4" />
                            Delete
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  </div>

                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    <p>Created: {new Date(category.createdAt).toLocaleDateString()}</p>
                    <p>Updated: {new Date(category.updatedAt).toLocaleDateString()}</p>
                  </div>
                </div>
              );
            })}

            {categories.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-20">
                <div className="w-32 h-32 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-16 h-16 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">No categories yet</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8 text-center max-w-md">
                  Create your first category to organize your blog content.
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Category
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add Category Modal */}
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Add New Category</h2>
              <form onSubmit={handleAddCategory}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Enter category name"
                    required
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-medium"
                  >
                    {submitting && <LoaderIcon className="w-4 h-4 animate-spin" />}
                    {submitting ? 'Creating...' : 'Create Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Category Modal */}
        {showEditModal && selectedCategory && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Edit Category</h2>
              <form onSubmit={handleEditCategory}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    placeholder="Enter category name"
                    required
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={closeModals}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-medium"
                  >
                    {submitting && <LoaderIcon className="w-4 h-4 animate-spin" />}
                    {submitting ? 'Updating...' : 'Update Category'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Approve Category Modal */}
        {showApproveModal && selectedCategory && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">Approve Category</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  Approve "<strong>{selectedCategory.name}</strong>"? It will become available for blogs.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={closeModals}
                    disabled={approving}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleApproveCategory}
                    disabled={approving}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-medium shadow-lg hover:shadow-xl"
                  >
                    {approving && <LoaderIcon className="w-4 h-4 animate-spin" />}
                    {approving ? 'Approving...' : 'Approve Category'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mark Pending Modal */}
        {showPendingModal && selectedCategory && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/20 rounded-full flex items-center justify-center">
                  <Clock3 className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
                  Mark as Pending
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  Set "<strong>{selectedCategory.name}</strong>" back to pending? It will be hidden from
                  public category pickers until approved again.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={closeModals}
                    disabled={markingPending}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkPendingCategory}
                    disabled={markingPending}
                    className="px-6 py-3 bg-amber-600 text-white rounded-xl hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-medium shadow-lg hover:shadow-xl"
                  >
                    {markingPending && <LoaderIcon className="w-4 h-4 animate-spin" />}
                    {markingPending ? 'Updating...' : 'Mark as Pending'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Category Modal */}
        {showDeleteModal && selectedCategory && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                  <Trash className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">Delete Category</h2>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  Are you sure you want to delete "<strong>{selectedCategory.name}</strong>"? This action
                  cannot be undone.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={closeModals}
                    disabled={deleting}
                    className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteCategory}
                    disabled={deleting}
                    className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors font-medium shadow-lg hover:shadow-xl"
                  >
                    {deleting && <LoaderIcon className="w-4 h-4 animate-spin" />}
                    {deleting ? 'Deleting...' : 'Delete Category'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </div>
  );
};

export default ManageBlogCategoriesPage;
